import { Link } from 'react-router-dom';
import useScrollReveal from '@/hooks/useScrollReveal';
import { PERKS } from '@/data/perks';

/**
 * PerksSection — homepage teaser for the Custom Perks system.
 *
 * @description Compact responsive grid of perk tiles (Celestial-tier icon,
 * name, tagline) with a CTA linking to the dedicated `/perks` showcase page. Mirrors
 * the ServerInfoSection pattern (scroll reveal, horror cards, staggered animation).
 *
 * @returns {JSX.Element} The custom perks teaser section.
 */
export default function PerksSection() {
  const sectionRef = useScrollReveal();

  return (
    <section id="perks" ref={sectionRef} className="py-20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16 scroll-reveal">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-orbitron horror-heading">
            Custom <span className="text-red-500">Perks</span>
          </h2>
          <div className="w-24 h-1 bg-red-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Eleven fully reworked perks with a deep prestige system — grind every tier from
            Iron all the way to Atomic and earn exclusive custom icons.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-12">
          {PERKS.map((perk, index) => (
            <div key={perk.key} className="scroll-reveal" style={{ animationDelay: `${(index + 1) * 0.1}s` }}>
              <Link
                to={`/perks?perk=${perk.key}`}
                className="horror-card group block h-full backdrop-blur-sm border border-red-900/30 bg-black/60 rounded-lg p-5 text-center shadow-2xl transition-all duration-300 hover:scale-[1.02] hover:border-red-600/60 hover:shadow-red-600/20"
              >
                <div className="relative mx-auto mb-4 flex h-20 w-20 items-center justify-center">
                  {perk.comingSoon ? (
                    <div className="flex h-20 w-20 items-center justify-center rounded-full border border-red-900/40 bg-gradient-to-br from-red-900/30 to-black">
                      <i className="ri-fire-line text-3xl text-red-500"></i>
                    </div>
                  ) : (
                    <img
                      src={`/perks/icons/${perk.key}/celestial.webp`}
                      alt={`${perk.name} perk icon`}
                      loading="lazy"
                      className="h-20 w-20 object-contain drop-shadow-[0_0_12px_rgba(220,38,38,0.35)] transition-transform duration-300 group-hover:scale-110"
                    />
                  )}
                  {perk.comingSoon && (
                    <span className="absolute -top-1 -right-1 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                      Soon
                    </span>
                  )}
                </div>
                <h3 className="text-base font-bold text-white font-orbitron leading-tight">{perk.name}</h3>
                <p className="mt-1.5 text-xs text-gray-400">{perk.tagline}</p>
              </Link>
            </div>
          ))}
        </div>

        <div className="text-center scroll-reveal">
          <Link
            to="/perks"
            className="pulse-glow inline-flex items-center gap-2 rounded-xl border-2 border-red-600 bg-red-600 px-8 py-4 text-lg font-bold text-white transition-all duration-300 hover:border-red-700 hover:bg-red-700 hover:shadow-red-600/25"
          >
            Explore All Perks
            <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
